import { ApiKey } from '@/injection/keys.ts';
import { injectStrict } from '@/injection/injectStrict.ts';
import { ApiRequestParams } from '@/types/api.type.ts';
import {
    Model,
    ModelCreateForm,
    ModelUpdateForm,
    ModelCreateFormErrors,
    ModelUpdateFormErrors,
    ModelCreateFormErrorsSchema,
    ModelUpdateFormErrorsSchema,
    ModelSchema,
    ModelsPaginator,
    ModelsPaginatorSchema,
    ModelsSchema,
    ModelsRequestFilter,
    ModelDeliveryPriceStatus,
    ModelsDeliveryPricesStatusesSchema,
} from '@/types/model.type.ts';
import { convertFlatMapToNestedMap } from '@/utils/collection-helpers.ts';

class ModelRepository
{
    private $Api: any;

    constructor() {
        this.$Api = injectStrict(ApiKey);
    }

    public getModel(id: number): Promise<Model>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('getModel', requestParams)
                .then(({ data }: any) => {
                    const parsedModel = ModelSchema.parse(data);
                    const model = parsedModel as unknown as Model;

                    resolve(model);
                });
        });
    }

    public getModels(modelsRequestFilter?: ModelsRequestFilter, oAbortController?: AbortController): Promise<ModelsPaginator>
    {
        const requestParams: ApiRequestParams = {
            query: modelsRequestFilter,
        };

        return new Promise((resolve) => {
            this.$Api.callNew('getModels', requestParams, null, oAbortController)
                .then(({ data }: any) => {
                    const parsedModelsPaginator = ModelsPaginatorSchema.parse(data);
                    const modelsPaginator: ModelsPaginator = parsedModelsPaginator as unknown as ModelsPaginator;

                    resolve(modelsPaginator);
                });
        });
    }

    public createModel(modelCreateForm: ModelCreateForm, oAbortController?: AbortController): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            requestBody: modelCreateForm,
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('createModel', requestParams, null, oAbortController)
                .then(() => resolve())
                .catch(({ response: { status, data: { errors } } }: any) => {
                    if (status === 422) {
                        const nestedErrors = convertFlatMapToNestedMap(errors);
                        const parsedErrors = ModelCreateFormErrorsSchema.parse(nestedErrors);
                        const createErrors: ModelCreateFormErrors = parsedErrors as unknown as ModelCreateFormErrors;

                        reject(createErrors);
                    }
                    else {
                        reject();
                    }
                });
        });
    }

    public updateModel(id: number, modelUpdateForm: ModelUpdateForm, oAbortController?: AbortController): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
            requestBody: modelUpdateForm,
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('updateModel', requestParams, null, oAbortController)
                .then(() => resolve())
                .catch(({ response: { status, data: { errors } } }: any) => {
                    if (status === 422) {
                        const nestedErrors = convertFlatMapToNestedMap(errors);
                        const parsedErrors = ModelUpdateFormErrorsSchema.parse(nestedErrors);
                        const updateErrors: ModelCreateFormErrors = parsedErrors as unknown as ModelUpdateFormErrors;

                        reject(updateErrors);
                    }
                    else {
                        reject();
                    }
                });
        });
    }

    public activateModel(id: number): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('activateModel', requestParams)
                .then(() => resolve());
        });
    }

    public deactivateModel(id: number): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('deactivateModel', requestParams)
                .then(() => resolve());
        });
    }

    public getAllActiveModels(): Promise<Model[]>
    {
        return new Promise((resolve) => {
            this.$Api.callNew('getAllActiveModels')
                .then(({ data }: any) => {
                    const parsedModels = ModelsSchema.parse(data);
                    const models: Model[] = parsedModels as unknown as Model[];

                    resolve(models);
                });
        });
    }

    public getModelsDeliveryPricesStatuses(model_ids: number[]): Promise<ModelDeliveryPriceStatus[]>
    {
        const requestParams: ApiRequestParams = {
            query: { model_ids },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('getModelsDeliveryPricesStatuses', requestParams)
                .then(({ data }: any) => {
                    const parsedStatuses = ModelsDeliveryPricesStatusesSchema.parse(data);
                    const statuses: ModelDeliveryPriceStatus[] = parsedStatuses as unknown as ModelDeliveryPriceStatus[];

                    resolve(statuses);
                });
        });
    }
}

export default ModelRepository;
